import { useAppDispatch, useAppSelector } from "../store/index";
import { useCallback, useState } from "react";
import {
  Button,
  Paper,
  Stack,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { setView, Views, setLastError } from "../store/session";
import { MainButton } from "../components/buttons";
import { FormGrid, GridItem1 } from "../components/grids";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_DETOX_SESSION_BY_ID,
  GET_DETOX_EVENTS_FOR_SESSION,
  SOFT_DELETE_DETOX_SESSION,
  UPDATE_DETOX_SESSION,
} from "../Queries/DetoxQueries";
import { DigitalDetoxSession } from "../model/detox";
import { computeStatus, formatDateForInput, mapGraphQLError, getTodayString, parseDateFromInput } from "../utils/detox";
import { DetoxEventManager } from "../components/DetoxEventManager";
import { isBefore, isValid } from "date-fns";
import ReactTimeAgo from "react-time-ago";
import dayjs from "dayjs";

// NOTE: Use CommonJS entrypoint to avoid webpack "fully specified" ESM resolution issues under CRA.
const { DatePicker } = require("@mui/x-date-pickers/DatePicker") as typeof import("@mui/x-date-pickers/DatePicker");

export const DetoxSessionDetail = () => {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((state) => state.session.viewDetoxSession?.id);
  const [session, setSession] = useState<DigitalDetoxSession | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  // Query for session data (dates, status, etc.)
  const { loading: sessionLoading } = useQuery(GET_DETOX_SESSION_BY_ID, {
    variables: { id: sessionId },
    skip: !sessionId,
    onCompleted: (data) => {
      if (data?.digital_detox_sessions_by_pk) {
        const detoxSession = data.digital_detox_sessions_by_pk as DigitalDetoxSession;
        setSession(detoxSession);
        setStartDate(formatDateForInput(detoxSession.start_date));
        setEndDate(formatDateForInput(detoxSession.end_date));
      }
    },
    onError: (error) => {
      dispatch(setLastError(error.message));
    },
  });

  // Separate query for events
  const { loading: eventsLoading, data: eventsData } = useQuery(GET_DETOX_EVENTS_FOR_SESSION, {
    variables: { session_id: sessionId },
    skip: !sessionId,
    onError: (error) => {
      dispatch(setLastError(error.message));
    },
  });

  const loading = sessionLoading || eventsLoading;

  const [updateSession, { loading: updating }] = useMutation(UPDATE_DETOX_SESSION, {
    onCompleted: () => {
      dispatch(setView(Views.DigitalDetox));
    },
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const [deleteSession] = useMutation(SOFT_DELETE_DETOX_SESSION, {
    onCompleted: () => {
      dispatch(setView(Views.DigitalDetox));
    },
    onError: (error) => {
      dispatch(setLastError(error.message));
    },
  });

  const validateDates = useCallback((): boolean => {
    if (!startDate || !endDate) {
      setValidationError("Start date and end date are required");
      return false;
    }

    const start = parseDateFromInput(startDate);
    const end = parseDateFromInput(endDate);

    if (!start || !end || !isValid(start) || !isValid(end)) {
      setValidationError("Invalid date format");
      return false;
    }

    if (isBefore(end, start)) {
      setValidationError("End date must be the same as or after start date");
      return false;
    }

    setValidationError("");
    return true;
  }, [startDate, endDate]);

  const handleSave = useCallback(async (cancelSession: boolean = false, uncancelSession: boolean = false) => {
    if (!validateDates() || !session) {
      return;
    }

    const setObject: any = {
      start_date: startDate,
      end_date: endDate,
    };

    // Set status: cancelled if cancelSession is true, compute from dates otherwise
    if (cancelSession) {
      setObject.status = 'cancelled';
    } else {
      const computedStatus = computeStatus(startDate, endDate, null);
      setObject.status = computedStatus;
    }

    await updateSession({
      variables: {
        pk_columns: { id: session.id },
        set: setObject,
      },
    });
  }, [session, startDate, endDate, validateDates, updateSession]);

  const handleDelete = useCallback(() => {
    if (session && window.confirm('Are you sure you want to delete this session?')) {
      deleteSession({
        variables: {
          id: session.id,
        },
      });
    }
  }, [session, deleteSession]);

  const handleCancel = useCallback(() => {
    dispatch(setView(Views.DigitalDetox));
  }, [dispatch]);

  if (loading) {
    return (
      <Stack spacing={2}>
        <Typography>Loading session details...</Typography>
      </Stack>
    );
  }

  if (!session) {
    return (
      <Stack spacing={2}>
        <Typography>Session not found</Typography>
        <Button onClick={handleCancel}>Back to List</Button>
      </Stack>
    );
  }

  // Use the stored status from database, don't compute
  const status = session.status || 'scheduled';
  const isCancelled = status === 'cancelled';
  const events = eventsData?.detox_events || [];
  
  // Get today's date in YYYY-MM-DD format for min date restriction
  const today = getTodayString();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'default';
      case 'scheduled':
        return 'primary';
      default:
        return 'default';
    }
  };

  const isLoading = updating;

  return (
    <Stack spacing={2}>
      <Stack direction={"row"} alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Manage Detox Session</Typography>
        <Stack direction="row" spacing={1}>
        <Button
            onClick={handleDelete}
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={isLoading}
          >
            Delete
          </Button>
          {isCancelled ? (
            <>
              <Button
                variant="contained"
                onClick={() => handleSave(false, true)}
                disabled={isLoading || !!validationError}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                Save & Uncancel
              </Button>
              <MainButton
                variant="contained"
                onClick={() => handleSave(false, false)}
                disabled={isLoading || !!validationError}
              >
                Save
              </MainButton>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => handleSave(true, false)}
                disabled={isLoading || !!validationError}
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  },
                }}
              >
                Save & Cancel Session
              </Button>
              <MainButton
                variant="contained"
                onClick={() => handleSave(false, false)}
                disabled={isLoading || !!validationError}
              >
                Save
              </MainButton>
            </>
          )}
        </Stack>
      </Stack>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        disabled={isLoading}
        sx={{ alignSelf: 'flex-start' }}
      >
        Back
      </Button>

      <Paper>
        <Stack spacing={2} padding={2}>
          {/* Status Display */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Status:</Typography>
            <Chip
              label={status.charAt(0).toUpperCase() + status.slice(1)}
              color={getStatusColor(status) as any}
              size="medium"
            />
          </Stack>

          <Divider />

          {/* Editable Dates and Cancelled Toggle */}
          {validationError && (
            <Typography color="error" variant="body2">
              {validationError}
            </Typography>
          )}

          <FormGrid>
            <GridItem1>
              <DatePicker
                label="Start Date"
                value={startDate ? dayjs(startDate) : null}
                disablePast
                disabled={isLoading}
                onChange={(newValue) => {
                  const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                  // Prevent selecting new dates before today
                  if (newDate && newDate < today) {
                    setValidationError("Start date cannot be before today");
                    return;
                  }
                  setStartDate(newDate);
                  setValidationError("");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    id: "start-date",
                    required: true,
                    error: !!validationError,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </GridItem1>
            <GridItem1>
              <DatePicker
                label="End Date"
                value={endDate ? dayjs(endDate) : null}
                disablePast
                disabled={isLoading}
                onChange={(newValue) => {
                  const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                  // Prevent selecting new dates before today
                  if (newDate && newDate < today) {
                    setValidationError("End date cannot be before today");
                    return;
                  }
                  setEndDate(newDate);
                  setValidationError("");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    id: "end-date",
                    required: true,
                    error: !!validationError,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </GridItem1>
          </FormGrid>

          <Divider />

          {/* Events Management */}
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Events ({events.length})
            </Typography>
            {sessionId && (
              <DetoxEventManager
                sessionId={sessionId}
                events={events}
              />
            )}
          </Stack>

          <Divider />

          {/* Metadata - Read-only section at bottom */}
          <Paper variant="outlined" sx={{ backgroundColor: 'grey.50', padding: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                Metadata (Read-only)
              </Typography>
              <Stack direction="row" spacing={4}>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    <ReactTimeAgo date={new Date(session.created_at)} />
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    <ReactTimeAgo date={new Date(session.updated_at)} />
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
};

